import { cloneDeep, isEqual, forEach } from 'lodash';
import noop from 'lodash/noop';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  AppPlatformDataAttributeCurrencyCondition,
  AttributeType,
  conditionFromDto,
  ConditionType,
} from 'src/actions/rules/condition';
import { Button } from 'src/presentation/components/agent_desktop_components';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import RootRuleErrors from './shared/RootRuleErrors';
import { RoutingPriority } from 'src/actions/rules/actions';
import { Row, Section } from 'src/presentation/components/forms/Layout';
import RuleActions from './RuleActions';
import rulesResource, { Rule } from 'src/actions/rules/rulesResource';
import RuleConditions from './RuleConditions';
import SmartMatchRuleRevision from './SmartMatchRuleRevision';
import { StyledBody } from './shared/StyledComponents';
import { TriggerType } from 'src/actions/rules/trigger';
import { useOnSave } from './shared/editorHelpers';
import validateRule from 'src/actions/rules/validateRule';

const dataAidPrefix = 'admin-smartMatch-ruleEditor';

const SmartMatchRuleEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { add, update, validate } = rulesResource.effects;
  const { resetErrors } = rulesResource.actions as any;
  const dispatch = useDispatch();

  const formItem = cloneDeep(item);
  forEach(formItem?.conditions, condition => {
    if (condition.type === ConditionType.AppPlatformDataAttribute && condition.attrType === AttributeType.Currency) {
      const translatedCondition = conditionFromDto(condition) as AppPlatformDataAttributeCurrencyCondition;
      condition.value = translatedCondition.value;
      condition.begin = translatedCondition.begin;
      condition.end = translatedCondition.end;
    }
  });

  const formContext = useForm({
    add,
    defaultItem: {
      name: '',
      disabled: false,
      conditions: [],
      actions: { setPriority: { priority: RoutingPriority.Low } },
      trigger: { type: TriggerType.SmartMatch },
      _version: 1,
    },
    errors,
    item: formItem,
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, overrideState, workingState } = formContext;

  const rule = useMemo(() => {
    return new Rule(workingState);
  }, [workingState]);

  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);
  const [isComplete, setComplete] = useState(false);

  const isModified = useMemo(() => !isEqual(formItem, workingState), [formItem, workingState]);

  const handleSave = useOnSave({
    ...formContext,
    add,
    resetErrors,
    setComplete,
    update,
    validateRule,
    workingState: rule,
  });

  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);

  const confirmCancel = useCallback(() => {
    isModified ? setConfirmCancelModalVisible(true) : onClose();
  }, [isModified, onClose, setConfirmCancelModalVisible]);

  const updateRule = useCallback(
    rule => {
      overrideState(rule.toJs());
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, overrideState, resetErrors]
  );

  const onChangeName = useCallback(
    e => {
      rule.setName(e.target.value);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onAddCondition = useCallback(
    (conditionType, attrName, attrType) => {
      rule.addCondition(conditionType, attrName, attrType);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onUpdateCondition = useCallback(
    ({ condition, index }) => {
      rule.updateCondition(condition, index);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onDeleteCondition = useCallback(
    index => {
      rule.deleteCondition(index);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onUpdateAction = useCallback(
    (key, value) => {
      rule.updateActionValue(key, value);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const query = useQuery();
  const revisionId = query.get('revisionId');
  if (revisionId) {
    return <SmartMatchRuleRevision revisionId={revisionId} />;
  }

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <H1 data-aid={`${dataAidPrefix}-title`}>{isNew ? 'Add' : 'Edit'} Priority Boost</H1>
        <StyledBody data-aid={dataAidPrefix}>
          <Section>
            <Row>
              <Input
                data-aid={`${dataAidPrefix}-nameInput`}
                attr="name"
                autoFocus
                label="Name"
                onChange={onChangeName}
                placeholder="Type Priority Boost name"
              />
            </Row>
          </Section>
          <RuleConditions
            conditions={rule.conditions}
            title={'Criteria'}
            errors={errors}
            triggerType={rule.trigger.type}
            onAddCondition={onAddCondition}
            onDeleteCondition={onDeleteCondition}
            onUpdateCondition={onUpdateCondition}
          />
          <RuleActions
            actions={rule.actions}
            title={'Boost Size'}
            errors={errors}
            triggerType={rule.trigger.type}
            onAddAction={noop}
            onDeleteAction={noop}
            onUpdateAction={onUpdateAction}
          />
          <RootRuleErrors errors={errors} />
        </StyledBody>
        <Footer>
          <Button data-aid={`${dataAidPrefix}-cancelButton`} type={Button.Types.TEXT} onClick={confirmCancel}>
            Cancel
          </Button>
          <Button data-aid={`${dataAidPrefix}-saveButton`} onClick={handleSave}>
            {isNew ? 'Add Priority Boost' : 'Save'}
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Priority Boost"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
      />
    </>
  );
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default SmartMatchRuleEditor;
